import {defaultConditon} from '@/context/Promotion/PromotionContext'
import {isInvalidTime, isStartDateLessThanCurrentDate} from '@/helpers/dateUtils'
import {
  ApiToUiDiscountType,
  ConditionGroup,
  NewDiscountType,
  Promotion,
  PromotionCondition,
  PromotionStep,
  PromotionType,
  Reward,
  RewardType,
} from '@/types/promotion'
import {deepMergeNoArray} from '../misc'
import {getValidPromotionConditions} from './promotionsValidationSchema'

const isEmpty = val => val == null || (typeof val === 'string' && !val.trim())

const hasConsistentRewardTypes = promotionConditions => {
  const rewardTypes = promotionConditions.map(c => c?.rewards?.[0]?.type).filter(Boolean)
  return new Set(rewardTypes).size <= 1
}

const isAmountCondition = condition => {
  return (
    condition?.items?.[0]?.uom === 'EA' ||
    (condition?.amount !== null && condition?.amount !== undefined) ||
    Array.isArray(condition?.layeredAmount)
  )
}

const getConditionBuyValue = (condition, forceAmount = false) => {
  if (forceAmount || isAmountCondition(condition)) {
    return condition?.amount
  }

  return condition?.quantity
}

const addEaUomToItems = items => {
  if (!Array.isArray(items) || items.length === 0) {
    return items || []
  }

  return items.map(item => ({...item, uom: 'EA'}))
}

const removeEaUomFromItems = items => {
  if (!Array.isArray(items) || items.length === 0) {
    return items || []
  }

  return items.map(item => {
    const {uom, ...rest} = item
    return rest
  })
}

const isDiscountReward = (reward?: Reward): reward is Extract<Reward, {type: RewardType.DISCOUNT}> => {
  return reward?.type === RewardType.DISCOUNT
}

const getRewardValue = (reward?: Reward) => {
  if (!reward) return undefined

  switch (reward.type) {
    case RewardType.DISCOUNT:
      return reward.discountValue
    case RewardType.POINTS:
      return reward.points
    case RewardType.FREE_PRODUCT:
    case RewardType.GIFT_ITEM:
      return reward.quantity
    default:
      return undefined
  }
}

const hasInvalidDiscountValues = (conditions: Promotion['promotionConditions']) => {
  return conditions.some(condition => {
    return condition?.rewards?.some(reward => {
      if (!isDiscountReward(reward) || reward.discountType !== 'PERCENTAGE') return false

      const hasInvalidDiscountValue =
        typeof reward.discountValue === 'number' && (reward.discountValue < 0 || reward.discountValue > 100)
      const hasInvalidDiscountAmountUpto =
        typeof reward.discountAmountUpto === 'number' && reward.discountAmountUpto < 0

      return hasInvalidDiscountValue || hasInvalidDiscountAmountUpto
    })
  })
}

const hasExceededMaxDiscountPercentage = (
  conditions: Promotion['promotionConditions'],
  maxDiscountPercentage: number
) => {
  return conditions.some(condition => {
    return condition?.rewards?.some(reward => {
      return (
        isDiscountReward(reward) &&
        reward.discountType === 'PERCENTAGE' &&
        typeof reward.discountValue === 'number' &&
        reward.discountValue > maxDiscountPercentage
      )
    })
  })
}

const hasInvalidIncludedSegments = (promotion: Promotion) => {
  return !promotion?.allCustomers && promotion?.segmentConfiguration?.includedSegments?.[0]?.segments?.length === 0
}

const hasInvalidExcludedSegments = (promotion: Promotion) => {
  return promotion?.segmentConfiguration?.excludedSegment?.segments?.length === 0
}

const hasInvalidLayeredConditions = (conditions: Promotion['promotionConditions']) => {
  for (let i = 1; i < conditions.length; i++) {
    const currentCondition = conditions[i]?.conditionGroup?.conditions?.[0]
    const currentBuyQuantity = getConditionBuyValue(currentCondition)
    const currentRewardValue = getRewardValue(conditions[i]?.rewards?.[0])

    const prevCondition = conditions[i - 1]
    const prevConditionValue = prevCondition?.conditionGroup?.conditions?.[0]
    const prevBuyQuantity = getConditionBuyValue(prevConditionValue)
    const prevRewardValue = getRewardValue(prevCondition?.rewards?.[0])

    if (prevBuyQuantity == null && typeof currentBuyQuantity === 'number') {
      return true
    }

    if (
      typeof prevBuyQuantity === 'number' &&
      typeof currentBuyQuantity === 'number' &&
      currentBuyQuantity <= prevBuyQuantity
    ) {
      return true
    }

    if (!currentRewardValue) return true

    if (prevRewardValue != null && currentRewardValue <= prevRewardValue) return true
  }
  return false
}

const isStepSetupInvalid = (promotion: Promotion, promotionConfig) => {
  if (!promotion?.name) return true

  const condition = promotion?.promotionConditions?.[0]
  if (!condition) return true

  const {validCount, totalCount} = getValidPromotionConditions(promotion.promotionConditions, promotion.promotionType)

  //When we save a promotion, we want to ensure that all the conditions are valid
  if (validCount !== totalCount) {
    return true
  }

  const conditions = promotion?.promotionConditions || []
  if (conditions.length > 1 && !hasConsistentRewardTypes(conditions)) {
    return true
  }

  if (hasInvalidDiscountValues(conditions)) {
    return true
  }

  const maxDiscountPercentage = promotionConfig?.maxDiscountPercentage || 0
  const promotionType = promotion?.promotionType
  const shouldCheckForMaxDiscount = [PromotionType.BUY_SINGLE_PRODUCT, PromotionType.BUY_MULTIPLE_PRODUCTS].includes(
    promotionType
  )

  if (shouldCheckForMaxDiscount && hasExceededMaxDiscountPercentage(conditions, maxDiscountPercentage)) {
    return true
  }

  if (promotion?.promotionType === PromotionType.BUY_SINGLE_PRODUCT && hasInvalidLayeredConditions(conditions)) {
    return true
  }

  if (hasInvalidIncludedSegments(promotion)) {
    return true
  }

  if (hasInvalidExcludedSegments(promotion)) {
    return true
  }

  return false
}

const isStepDisplayInvalid = (promotion, languages, isIndia) => {
  if (isEmpty(promotion?.bannerUrl) && !isIndia) return true

  if (promotion?.bannerType === 'clickable' && !promotion?.landingPage) {
    return true
  }

  if (
    promotion?.displayOrder === false ||
    promotion?.displayOrder === 0 ||
    promotion?.displayOrder === null ||
    promotion?.displayOrder === undefined
  ) {
    return true
  }

  const text = promotion?.text || {}
  const isTextComplete = lang => {
    const localized = text[lang]
    if (isIndia) {
      return localized?.title && localized?.productTag
    }
    return localized?.title && localized?.bannerTag && localized?.productTag
  }

  const langCodes = languages?.length ? languages.map(l => l.code) : Object.keys(text)

  return !langCodes.every(isTextComplete)
}

const isStepAttributesInvalid = promotion => {
  if (
    !promotion?.activeStartDate ||
    !promotion?.activeStartTime ||
    !promotion?.activeEndDate ||
    !promotion?.activeEndTime
  ) {
    return true
  }

  if (
    promotion?.activeStartDate >= promotion?.activeEndDate ||
    isStartDateLessThanCurrentDate(promotion.activeStartDate)
  ) {
    return true
  }

  // check for invalid time
  if (isInvalidTime(promotion?.activeStartTime) || isInvalidTime(promotion?.activeEndTime)) return true

  return false
}

export const getIsNextDisabled = (step, promotion, languages, promotionConfig, isIndia) => {
  switch (step) {
    case PromotionStep.SETUP:
      return isStepSetupInvalid(promotion, promotionConfig)
    case PromotionStep.DISPLAY:
      return isStepDisplayInvalid(promotion, languages, isIndia)
    case PromotionStep.ATTRIBUTES:
      return isStepAttributesInvalid(promotion)
    default:
      return false
  }
}

//For drafts we can save with partial conditions but don't want to return them in the draft
export const parsePromotionDraft = promotion => {
  const parsedPromotion = JSON.parse(JSON.stringify(promotion))
  const {hasValid} = getValidPromotionConditions(promotion.promotionConditions, promotion.promotionType)
  parsedPromotion.promotionConditions = hasValid ? promotion.promotionConditions : []
  return parsedPromotion
}

export function mergePromotionConditions(
  promotionConditions: PromotionCondition[],
  updates: Partial<PromotionCondition>
): PromotionCondition[] {
  return promotionConditions.map(pc => {
    if (pc.sequence !== updates.sequence) return pc

    // 1) Merge top-level fields
    const mergedPC: PromotionCondition = deepMergeNoArray(pc, updates)

    // 2) Merge nested conditionGroup.conditions by index, if provided
    if (updates.conditionGroup?.conditions) {
      const originalConds = pc.conditionGroup?.conditions ?? []
      const incomingConds = updates.conditionGroup.conditions

      mergedPC.conditionGroup = {
        ...(pc.conditionGroup ?? {}),
        ...updates.conditionGroup,
        conditions: originalConds.length
          ? originalConds.map((orig, idx) => deepMergeNoArray(orig, incomingConds[idx] ?? {}))
          : incomingConds,
      } as ConditionGroup
    }

    // 3) Merge rewards array by index, if provided
    if (updates.rewards) {
      const originalRewards = pc.rewards ?? []
      const incomingRewards = updates.rewards

      mergedPC.rewards = originalRewards.length
        ? originalRewards.map((orig, idx) => deepMergeNoArray(orig, incomingRewards[idx] ?? {}))
        : incomingRewards
    }

    return mergedPC
  })
}

export const createPromotionConditions = (type: PromotionType) => {
  switch (type) {
    case PromotionType.BUY_SINGLE_PRODUCT:
      return [
        {
          sequence: 1,
          conditionGroup: {
            conditions: [defaultConditon],
          },
        },
      ]
    case PromotionType.BUY_MULTIPLE_PRODUCTS:
      return [
        {
          sequence: 1,
          conditionGroup: {
            conditions: [defaultConditon, defaultConditon],
          },
        },
      ]
    default:
      return [
        {
          sequence: 1,
        },
      ]
  }
}
const layeredRewardFieldMap: Record<string, string> = {
  points: 'layeredPoints',
  discountValue: 'layeredDiscountValue',
  discountAmountUpto: 'layeredDiscountAmountUpto',
  quantity: 'layeredQuantity',
}

const buildLayeredReward = (conditionsArray, baseReward) => {
  const multiLayerReward = {
    type: baseReward?.type,
    ...(baseReward?.discountType && {discountType: baseReward.discountType}),
    ...(baseReward?.items && {items: baseReward.items}),
    ...(baseReward?.itemType && {itemType: baseReward.itemType}),
  }

  Object.entries(layeredRewardFieldMap).forEach(([field, layeredField]) => {
    const layeredValues = conditionsArray
      .map(layer => layer?.rewards?.[0]?.[field])
      .filter(value => value !== null && value !== undefined)

    if (layeredValues.length > 0) {
      multiLayerReward[layeredField] = layeredValues
    }
  })

  if (baseReward?.type === 'DISCOUNT') {
    multiLayerReward.discountAppliedOn = null
  }

  return multiLayerReward
}

export const buildSingleLayerReward = (reward, index, isLayered, promotionType) => {
  const isBuyProductPromo =
    promotionType === PromotionType.BUY_SINGLE_PRODUCT || promotionType === PromotionType.BUY_MULTIPLE_PRODUCTS

  const singleLayerReward = {
    type: reward?.type,
    ...(reward?.discountType && {
      discountType: isBuyProductPromo ? reward.discountType : NewDiscountType[promotionType],
    }),
    ...(reward?.items && {items: reward.items}),
    ...(reward?.itemType && {itemType: reward.itemType}),
  }

  Object.entries(layeredRewardFieldMap).forEach(([field, layeredField]) => {
    const value = isLayered ? (reward?.[layeredField]?.[index] ?? null) : (reward?.[field] ?? null)

    if (value !== null) {
      singleLayerReward[field] = value
    }
  })

  if (reward?.type === 'DISCOUNT') {
    singleLayerReward.discountAppliedOn = reward?.discountAppliedOn ?? null
  }

  return singleLayerReward
}

export const transformToLayeredPromotion = (promotion: any) => {
  const conditionsArray = promotion?.promotionConditions || []

  if (!Array.isArray(conditionsArray) || conditionsArray.length === 0) {
    return promotion
  }

  const firstLayer = conditionsArray[0]
  const isSingleLayer = conditionsArray.length === 1
  const baseCondition = firstLayer?.conditionGroup?.conditions?.[0]
  const baseReward = firstLayer?.rewards?.[0]
  const isAmountBasedCondition = isAmountCondition(baseCondition)
  const conditionField = isAmountBasedCondition ? 'amount' : 'quantity'
  const layeredConditionField = isAmountBasedCondition ? 'layeredAmount' : 'layeredQuantity'

  const promoType = promotion.uiPromotionType ?? promotion.promotionType

  const baseConditionWithoutQuantity = Object.fromEntries(
    Object.entries(baseCondition ?? {}).filter(
      ([key]) =>
        !['quantity', 'amount', 'layeredQuantity', 'layeredAmount', 'layeredPoints', 'layeredDiscountValue'].includes(
          key
        )
    )
  )

  const quantityField = isSingleLayer
    ? {[conditionField]: getConditionBuyValue(baseCondition, isAmountBasedCondition)}
    : {
        [layeredConditionField]: conditionsArray
          .map(layer => getConditionBuyValue(layer?.conditionGroup?.conditions?.[0], isAmountBasedCondition))
          .filter((q): q is number => typeof q === 'number'),
      }

  const reward = !isSingleLayer
    ? buildLayeredReward(conditionsArray, baseReward)
    : buildSingleLayerReward(baseReward, 0, false, promoType)

  let conditions
  if (promoType === PromotionType.BUY_MULTIPLE_PRODUCTS) {
    const firstConditions = firstLayer?.conditionGroup?.conditions || []

    conditions = firstConditions.map((cond: any, condIndex: number) => {
      const useAmountField = isAmountCondition(cond)
      const currentConditionField = useAmountField ? 'amount' : 'quantity'
      const currentLayeredConditionField = useAmountField ? 'layeredAmount' : 'layeredQuantity'
      const rest = {...cond}
      delete rest.type
      delete rest.quantity
      delete rest.amount
      delete rest.layeredQuantity
      delete rest.layeredAmount

      const layeredBuyValues = conditionsArray
        .map(layer => getConditionBuyValue(layer?.conditionGroup?.conditions?.[condIndex], useAmountField))
        .filter((q): q is number => typeof q === 'number')

      return {
        ...rest,
        ...(isSingleLayer
          ? {[currentConditionField]: getConditionBuyValue(cond, useAmountField)}
          : {[currentLayeredConditionField]: layeredBuyValues}),
        items: useAmountField ? removeEaUomFromItems(rest.items) : rest.items,
      }
    })
  } else {
    conditions = [
      {
        ...baseConditionWithoutQuantity,
        ...quantityField,
        items: isAmountBasedCondition ? removeEaUomFromItems(baseCondition?.items) : baseCondition?.items || [],
      },
    ]
  }

  const newPromotion = {
    ...promotion,
    promotionCondition: {
      conditionGroup: {
        conditions,
        ...(promoType === PromotionType.BUY_MULTIPLE_PRODUCTS && {
          combinedWith: 'AND',
        }),
      },
      reward,
    },
  }

  if (promoType === PromotionType.WELCOME_DISCOUNT || promoType === PromotionType.STORE_DISCOUNT) {
    delete newPromotion?.promotionCondition?.conditionGroup
    delete newPromotion?.maxTimesPerOrder
    delete newPromotion?.maxFreeItemsPerOrder
  }

  if (promoType === PromotionType.WELCOME_DISCOUNT) { 
    delete newPromotion?.resetFrequencyInDays
  }

  delete newPromotion.promotionConditions
  delete newPromotion.promotionType

  return newPromotion
}
export const transformToMultiLayerPromotion = (promotion: any) => {
  const layered = promotion?.promotionCondition
  const promotionType = promotion?.uiPromotionType

  if (promotionType === PromotionType.WELCOME_DISCOUNT || promotionType === PromotionType.STORE_DISCOUNT) {
    const reward = layered?.reward

    if (!reward) return promotion

    const promotionWithoutCondition = Object.fromEntries(
      Object.entries(promotion).filter(([key]) => key !== 'promotionCondition')
    )

    return {
      ...promotionWithoutCondition,
      promotionType,
      promotionConditions: [
        {
          sequence: 1,
          rewards: [
            {
              type: reward.type,
              discountType: ApiToUiDiscountType[reward?.discountType],
              discountValue: reward.discountValue,
              discountAmountUpto: reward.discountAmountUpto,
            },
          ],
        },
      ],
    }
  }

  if (!layered?.conditionGroup) return promotion

  const reward = layered.reward
  const baseConditions = layered.conditionGroup.conditions

  if (!Array.isArray(baseConditions) || !reward) return promotion

  const combinedWith = layered.conditionGroup.combinedWith

  const isBuyMultiple = promotion?.uiPromotionType === PromotionType.BUY_MULTIPLE_PRODUCTS

  let promotionConditions

  if (isBuyMultiple) {
    const hasLayeredConditions = baseConditions.some(
      (cond: any) => Array.isArray(cond?.layeredQuantity) || Array.isArray(cond?.layeredAmount)
    )
    const hasLayeredRewards = Object.values(layeredRewardFieldMap).some(layeredField =>
      Array.isArray(reward?.[layeredField])
    )
    const isLayered = hasLayeredConditions || hasLayeredRewards

    if (isLayered) {
      const layerCount =
        baseConditions.reduce((max, cond) => {
          return Math.max(max, cond?.layeredQuantity?.length || 0, cond?.layeredAmount?.length || 0)
        }, 0) || Math.max(...Object.values(layeredRewardFieldMap).map(f => reward?.[f]?.length || 0))

      promotionConditions = Array.from({length: layerCount}, (_, layerIndex) => ({
        sequence: layerIndex + 1,
        conditionGroup: {
          conditions: baseConditions.map((cond: any) => {
            const hasLayeredAmount = Array.isArray(cond?.layeredAmount)
            const condField = hasLayeredAmount ? 'amount' : 'quantity'
            const layeredField = hasLayeredAmount ? 'layeredAmount' : 'layeredQuantity'

            const cleanCond = Object.fromEntries(
              Object.entries(cond).filter(
                ([key]) => !['layeredQuantity', 'layeredAmount', 'quantity', 'amount'].includes(key)
              )
            )

            return {
              ...cleanCond,
              type: 'BUY',
              [condField]: cond[layeredField]?.[layerIndex],
              items: hasLayeredAmount ? addEaUomToItems(cleanCond.items) : cleanCond.items,
            }
          }),
          ...(combinedWith && {combinedWith}),
        },
        rewards: [buildSingleLayerReward(reward, layerIndex, true, promotionType)],
      }))
    } else {
      const isAmountBasedCondition = isAmountCondition(baseConditions[0])
      const normalizedConditions = baseConditions.map((cond: any) => ({
        ...cond,
        type: 'BUY',
        items: isAmountBasedCondition ? addEaUomToItems(cond.items) : cond.items,
      }))

      const rewardObj = reward.type === 'POINTS' ? {...reward, discountType: 'POINTS'} : {...reward}

      promotionConditions = [
        {
          sequence: 1,
          conditionGroup: {
            conditions: normalizedConditions,
            ...(combinedWith && {combinedWith}),
          },
          rewards: [rewardObj],
        },
      ]
    }
  } else {
    const baseCondition = baseConditions?.[0] || {}
    const isAmountBasedCondition = isAmountCondition(baseCondition)
    const layeredConditionField = isAmountBasedCondition ? 'layeredAmount' : 'layeredQuantity'
    const conditionField = isAmountBasedCondition ? 'amount' : 'quantity'
    const layeredValues = baseCondition?.[layeredConditionField]

    const isLayered =
      Array.isArray(layeredValues) &&
      Object.values(layeredRewardFieldMap).some(layeredField => Array.isArray(reward?.[layeredField]))

    const isSingle = typeof getConditionBuyValue(baseCondition, isAmountBasedCondition) === 'number'

    if (!isLayered && !isSingle) return promotion

    const quantities = isLayered
      ? layeredValues
      : baseConditions.map(c => getConditionBuyValue(c, isAmountBasedCondition))

    promotionConditions = quantities.map((qty: number, index: number) => {
      const baseCondition = baseConditions[0]

      const baseConditionWithoutLayeredQuantity = Object.fromEntries(
        Object.entries(baseCondition).filter(
          ([key]) => !['layeredQuantity', 'layeredAmount', 'quantity', 'amount'].includes(key)
        )
      )

      const baseConditionClean = {
        ...baseConditionWithoutLayeredQuantity,
        type: 'BUY',
        [conditionField]: qty,
        items: isAmountBasedCondition ? addEaUomToItems(baseCondition.items) : baseCondition.items || [],
      }

      return {
        sequence: index + 1,
        conditionGroup: {
          conditions: [baseConditionClean],
        },
        rewards: [buildSingleLayerReward(reward, index, isLayered, promotionType)],
      }
    })
  }

  const newPromotion = {
    ...promotion,
    promotionConditions,
    promotionType,
  }

  delete newPromotion.promotionCondition
  return newPromotion
}
